import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  Icon,
  Image,
  Input,
  Modal,
  Popup,
  Select,
} from 'semantic-ui-react';
import { capitalize } from 'lodash';

import * as EditorHelpers from '../helpers/Editor';

class Toolbar extends Component {
  static propTypes = {
    value: PropTypes.object,
    token: PropTypes.string,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        Token: PropTypes.string,
        Description: PropTypes.string,
      })
    ),
    pageSize: PropTypes.string,
    pageSizes: PropTypes.arrayOf(PropTypes.string),
    fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fontSizes: PropTypes.arrayOf(PropTypes.number),
    fontFamily: PropTypes.string,
    fontFamilies: PropTypes.arrayOf(PropTypes.string),
    margins: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
    }),
    marginSizes: PropTypes.arrayOf(PropTypes.number),
    onMark: PropTypes.func,
    onImageUrl: PropTypes.func,
    onImageInput: PropTypes.func,
    onInsert: PropTypes.func,
    onAlign: PropTypes.func,
    onFontFamily: PropTypes.func,
    onFontSize: PropTypes.func,
    onMargins: PropTypes.func,
    onToken: PropTypes.func,
    onPageSize: PropTypes.func,
    onUndo: PropTypes.func,
    onRedo: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.imageFileInputRef = React.createRef();
    this.imageTextInputRef = React.createRef();
    this.state = {
      imageUrlModalOpened: false,
      imageUrl: '',
      imageInsertButtonDisabled: true,
    };
  }

  onToken = (e, data) => {
    const selectedToken = data.value;
    this.props.onToken(selectedToken);
  };

  renderTokenSelect = () => {
    const { tokens, token, onInsert } = this.props;
    const options = tokens.map(({ Token, Description }) => ({
      value: Token,
      text: Description ? Description : Token,
    }));
    return (
      <React.Fragment>
        <Select
          placeholder={options.length === 0 ? 'No fields' : 'Available fields'}
          options={options}
          onChange={this.onToken}
          value={token}
        />
        <Button.Group>
          <Button compact onClick={onInsert} content="Insert" />
        </Button.Group>
      </React.Fragment>
    );
  };

  onFontSizeSelect = (e, data) => {
    const { ptToPx } = EditorHelpers;
    const selectedFontSize = data.value;
    this.props.onFontSize(Math.round(ptToPx(selectedFontSize)));
  };

  renderFontSizeSelect = () => {
    let { fontSizes, fontSize } = this.props;
    const { pxToPt } = EditorHelpers;
    const options = fontSizes.map(fs => ({
      value: fs,
      text: fs,
    }));
    /**
     * Strip px and convert to number
     * "14px" => 14
     */
    if (typeof fontSize === 'string') {
      fontSize = fontSize.replace('px', '');
      fontSize = parseInt(fontSize, 10);
    }
    return (
      <Select
        options={options}
        onChange={this.onFontSizeSelect}
        value={Math.round(pxToPt(fontSize))}
      />
    );
  };

  onFontFamilySelect = (e, data) => {
    const selectedFontFamily = data.value;
    this.props.onFontFamily(selectedFontFamily);
  };

  renderFontFamilySelect = () => {
    const { fontFamilies, fontFamily } = this.props;
    const options = fontFamilies.sort().map(ff => ({
      value: ff,
      text: ff,
    }));
    return (
      <Select
        options={options}
        onChange={this.onFontFamilySelect}
        value={fontFamily}
      />
    );
  };

  onAlign = alignment => () => this.props.onAlign(alignment);

  renderAlignment = () => {
    const { value } = this.props;
    const modKey = EditorHelpers.getModKey();
    return (
      <Button.Group>
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Left align (${modKey}+Shift+L)`}
          trigger={
            <Button
              icon="align left"
              active={EditorHelpers.hasBlockWithData(value, 'alignment', {
                alignment: 'left',
              })}
              onClick={this.onAlign('left')}
            />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Center align (${modKey}+Shift+E)`}
          trigger={
            <Button
              active={EditorHelpers.hasBlockWithData(value, 'alignment', {
                alignment: 'center',
              })}
              icon="align center"
              onClick={this.onAlign('center')}
            />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Right align (${modKey}+Shift+R)`}
          trigger={
            <Button
              active={EditorHelpers.hasBlockWithData(value, 'alignment', {
                alignment: 'right',
              })}
              icon="align right"
              onClick={this.onAlign('right')}
            />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Justify align (${modKey}+Shift+J)`}
          trigger={
            <Button
              active={EditorHelpers.hasBlockWithData(value, 'alignment', {
                alignment: 'justify',
              })}
              icon="align justify"
              onClick={this.onAlign('justify')}
            />
          }
        />
      </Button.Group>
    );
  };

  onMark = mark => () => this.props.onMark(mark);

  renderTextMarks = () => {
    const { value } = this.props;
    const modKey = EditorHelpers.getModKey();
    return (
      <Button.Group>
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Bold (${modKey}+B)`}
          trigger={
            <Button
              icon="bold"
              active={EditorHelpers.hasMark(value, 'bold')}
              onClick={this.onMark('bold')}
            />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Italics (${modKey}+I)`}
          trigger={
            <Button
              icon="italic"
              active={EditorHelpers.hasMark(value, 'italic')}
              onClick={this.onMark('italic')}
            />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Underlined (${modKey}+U)`}
          trigger={
            <Button
              icon="underline"
              active={EditorHelpers.hasMark(value, 'underlined')}
              onClick={this.onMark('underlined')}
            />
          }
        />
      </Button.Group>
    );
  };

  toggleImageModal = (opened = false) =>
    this.setState({
      imageUrlModalOpened: opened,
      imageUrl: '',
      imageInsertButtonDisabled: true,
    });

  onImageFiles = event => {
    if (!event.target.files[0]) {
      return;
    }
    const files = event.target.files;
    this.props.onImageFiles(files);
  };

  triggerImageFileInput = () => {
    this.imageFileInputRef.current.click();
  };

  renderImageInsert = () => {
    const { onImageUrl } = this.props;
    const {
      imageUrlModalOpened,
      imageUrl,
      imageInsertButtonDisabled,
    } = this.state;
    return (
      <React.Fragment>
        <Button.Group>
          <Dropdown button icon="dropdown" text="Insert Image">
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => this.toggleImageModal(true)}
                text="By URL"
              />
              <Dropdown.Item onClick={this.triggerImageFileInput}>
                <span>Upload from computer</span>
                <input
                  style={{ display: 'none' }}
                  ref={this.imageFileInputRef}
                  type="file"
                  onChange={this.onImageFiles}
                />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Button.Group>
        <Modal
          dimmer
          size="mini"
          open={imageUrlModalOpened}
          onClose={() => this.toggleImageModal(false)}
        >
          <Modal.Header>Insert image</Modal.Header>
          <Modal.Content>
            {EditorHelpers.isUrl(imageUrl) && (
              <Image
                onLoad={() => {
                  if (imageInsertButtonDisabled) {
                    this.setState({ imageInsertButtonDisabled: false });
                  }
                }}
                onError={() => {
                  if (!imageInsertButtonDisabled) {
                    this.setState({ imageInsertButtonDisabled: true });
                  }
                }}
                fluid
                src={imageUrl}
              />
            )}
            <Input
              ref={this.imageTextInputRef}
              type="text"
              onChange={(e, data) => {
                this.setState({ imageUrl: data.value });
              }}
              fluid
              placeholder="Paste URL of image..."
            />
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => this.toggleImageModal(false)} negative>
              Cancel
            </Button>
            <Button
              disabled={imageInsertButtonDisabled}
              onClick={() => {
                onImageUrl(imageUrl);
                this.toggleImageModal(false);
              }}
              primary
            >
              Insert
            </Button>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    );
  };

  onMarginSelect = which => (e, data) => {
    this.props.onMargins(which, data.value);
  };

  renderMarginSelect = () => {
    const { margins, marginSizes } = this.props;
    const options = marginSizes.map(size => ({
      text: `${size}"`,
      value: size,
    }));
    return (
      <Select
        placeholder="Margins"
        options={options}
        onChange={this.onMarginSelect('all')}
        value={margins.top} // all margins are the same
      />
    );
  };

  onPageSizeSelect = (e, data) => {
    this.props.onPageSize(data.value);
  };

  renderPageSizeSelect = () => {
    const { pageSizes, pageSize } = this.props;
    const options = pageSizes.map(ps => ({ value: ps, text: capitalize(ps) }));
    return (
      <Select
        options={options}
        onChange={this.onPageSizeSelect}
        value={pageSize}
      />
    );
  };

  undo = this.props.onUndo;
  redo = this.props.onRedo;

  renderUndoRedo() {
    const { hasUndo, hasRedo } = this.props;
    const modKey = EditorHelpers.getModKey();

    return (
      <Button.Group>
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Undo (${modKey}+Z)`}
          trigger={
            <Button disabled={!hasUndo} icon="undo" onClick={this.undo} />
          }
        />
        <Popup
          mouseEnterDelay={500}
          position="bottom left"
          content={`Redo (${modKey}+Shift+Z)`}
          trigger={
            <Button disabled={!hasRedo} icon onClick={this.redo}>
              <Icon flipped="horizontally" name="undo" />
            </Button>
          }
        />
      </Button.Group>
    );
  }

  renderName = () => <h3 className="text-orange">{this.props.name}</h3>;

  render() {
    return (
      <div id="toolbar">
        <div className="toolbar-rows">
          <div className="toolbar-row toolbar-title-row">
            {this.renderName()}
          </div>
          <div className="toolbar-row">
            {this.renderPageSizeSelect()}
            {this.renderMarginSelect()}
            {this.renderFontFamilySelect()}
            {this.renderFontSizeSelect()}
            {this.renderTokenSelect()}
          </div>
          <div className="toolbar-row">
            {this.renderAlignment()}
            {this.renderTextMarks()}
            {this.renderImageInsert()}
            {this.renderUndoRedo()}
          </div>
        </div>
      </div>
    );
  }
}
export default Toolbar;



// WEBPACK FOOTER //
// ./src/pages/lettersetup/components/Toolbar.js